import Box from '@mui/material/Box';
import React, { memo } from 'react';
import LayoutWrapperBottombar from './LayoutWrapperBottombar/LayoutWrapperBottombar';
import LayoutWrapperFooter from './LayoutWrapperFooter/LayoutWrapperFooter';
import LayoutWrapperMain from './LayoutWrapperMain';
import LayoutWrapperTopbar from './LayoutWrapperTopbar/LayoutWrapperTopbar';

const layoutSideNavigationMainStyles = {
  display: 'flex',
  flexGrow: 1,
  flexShrink: 0,
  flexDirection: 'column',
  justifyContent: 'center',
  width: '100%',
};

/**
 * Prepare children for LayoutSingleColumn.
 * @param {React.JSX.Element | React.JSX.Element[]} children
 * @returns {Record<string, React.JSX.Element | null>}
 */
const prepareChildren = (children) => {
  /** @type {Record<string, React.JSX.Element | null>} */
  const childrenMap = {
    layoutWrapperTopbar: null,
    layoutWrapperMain: null,
    layoutWrapperFooter: null,
    LayoutWrapperBottombar: null,
  };

  React.Children.forEach(children, (child) => {
    if (!child) {
      // do nothing
    } else if (child.type === LayoutWrapperTopbar) {
      childrenMap.layoutWrapperTopbar = child;
    } else if (child.type === LayoutWrapperMain) {
      // LayoutWrapperMain needs different rootClassName
      childrenMap.layoutWrapperMain = React.cloneElement(child, {
        sx: { ...layoutSideNavigationMainStyles, ...(child.props.sx ? child.props.sx : {}) },
      });
    } else if (child.type === LayoutWrapperFooter) {
      childrenMap.layoutWrapperFooter = child;
    } else if (child.type === LayoutWrapperBottombar) {
      childrenMap.LayoutWrapperBottombar = child;
    } else {
      throw new Error(
        `LayoutSingleColumn has an unknown child.
      Only LayoutWrapperTopbar, LayoutWrapperMain, LayoutWrapperFooter are allowed.`
      );
    }
  });
  return childrenMap;
};

const LayoutSingleColumnStyles = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
};

/**
 * LayoutSingleColumn needs to have 2-3 children:
 * LayoutWrapperTopbar, LayoutWrapperMain, and possibly LayoutWrapperFooter.
 * @param {object} props
 * @param {React.JSX.Element[]} props.children - Collection of children to be rendered.
 * @returns {React.JSX.Element}
 */

const LayoutSingleColumnComponent = ({ children }) => {
  const preparedChildren = prepareChildren(children);
  const maybeFooter = preparedChildren.layoutWrapperFooter || null;
  const maybeBottomBar = preparedChildren.LayoutWrapperBottombar || null;

  return (
    <Box sx={LayoutSingleColumnStyles}>
      {preparedChildren.layoutWrapperTopbar || null}
      {preparedChildren.layoutWrapperMain || null}
      {maybeFooter}
      {maybeBottomBar}
    </Box>
  );
};

LayoutSingleColumnComponent.displayName = 'LayoutSingleColumn';

const LayoutSingleColumn = memo(LayoutSingleColumnComponent);

export default LayoutSingleColumn;
